import type { CSSProperties, ReactNode } from "react";

interface RuleSpineProps {
  keywords?: ReactNode;
  tickers?: ReactNode;
  tags?: ReactNode;
  /** Between Keywords and the rest. */
  keywordSwitch?: ReactNode;
  /** Between Tickers and Tags. */
  filterSwitch?: ReactNode;
  /** Tickers and Tags drawn together — see RuleShape.frame. */
  frame?: "either" | "both";
  /** One line inside the frame: the swamping warning. */
  hint?: string;
}

/** Where a row sits on the spine, which decides the stretch of line it draws. */
type Place = "first" | "middle" | "last" | "only";

/** Height of a box's title row, where its elbow meets the spine. */
const ELBOW = 22;
/** The frame's caption sits a little higher than a box title. */
const FRAME_ELBOW = 19;

/**
 * The boxes on one spine, joined by their switches, all at one indent: they
 * are filled in any order, and the only time the server's grouping of
 * Tickers with Tags changes anything — when the two switches disagree — the
 * pair gets a light frame and a caption saying so.
 *
 * Boxes left out (a tag watchlist edited in place shows only Tags) take
 * their switches with them.
 */
export default function RuleSpine({
  keywords,
  tickers,
  tags,
  keywordSwitch,
  filterSwitch,
  frame,
  hint,
}: RuleSpineProps) {
  const pair = !!tickers && !!tags;
  const framed = !!frame && pair && !!keywords;

  type Row = { key: string; kind: "box" | "joint" | "frame"; node: ReactNode };
  const rows: Row[] = [];
  const pairRows: Row[] = [];
  if (tickers) pairRows.push({ key: "tickers", kind: "box", node: tickers });
  if (pair) pairRows.push({ key: "fm", kind: "joint", node: filterSwitch });
  if (tags) pairRows.push({ key: "tags", kind: "box", node: tags });

  if (keywords) rows.push({ key: "keywords", kind: "box", node: keywords });
  if (keywords && (tickers || tags)) {
    rows.push({ key: "kop", kind: "joint", node: keywordSwitch });
  }
  if (framed) rows.push({ key: "frame", kind: "frame", node: null });
  else rows.push(...pairRows);

  const placeOf = (index: number): Place =>
    rows.length === 1
      ? "only"
      : index === 0
        ? "first"
        : index === rows.length - 1
          ? "last"
          : "middle";

  return (
    <div className="flex flex-col pl-7">
      {rows.map((row, index) => {
        const place = placeOf(index);
        if (row.kind === "joint") {
          return (
            <div key={row.key} className="relative flex h-11 items-center">
              <Spine place="middle" elbow={ELBOW} />
              {row.node}
            </div>
          );
        }
        if (row.kind === "frame") {
          return (
            <div
              key={row.key}
              className="relative rounded-lg border border-gray-700 bg-gray-900/55 px-3 pb-3 pt-2.5"
            >
              <Spine place={place} elbow={FRAME_ELBOW} offset={18} />
              <p className="m-0 mb-2 text-xs text-gray-400">
                {frame === "either" ? "either of these" : "both of these"}
              </p>
              {pairRows.map((inner) =>
                inner.kind === "joint" ? (
                  <div key={inner.key} className="flex h-11 items-center">
                    {inner.node}
                  </div>
                ) : (
                  <div key={inner.key}>{inner.node}</div>
                )
              )}
              {hint && (
                <p className="m-0 mt-2.5 flex items-center gap-2 text-xs text-gray-300">
                  <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-amber-500" />
                  {hint}
                </p>
              )}
            </div>
          );
        }
        return (
          <div key={row.key} className="relative">
            <Spine place={place} elbow={ELBOW} />
            {row.node}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The stretch of spine a row draws, and its elbow: from the elbow down for
 * the first row, down to the elbow for the last, all the way for the rest.
 */
function Spine({ place, elbow, offset = 17 }: { place: Place; elbow: number; offset?: number }) {
  if (place === "only") return null;
  const line: CSSProperties =
    place === "first"
      ? { top: elbow, bottom: 0 }
      : place === "last"
        ? { top: 0, height: elbow + 1 }
        : { top: 0, bottom: 0 };
  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute w-px bg-gray-700"
        style={{ ...line, left: -offset }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute h-px bg-gray-700"
        style={{ top: elbow, left: -offset, width: offset }}
      />
    </>
  );
}
